import React, { useState, useEffect, useRef } from "react";
import {
  Icon,
  Table,
  Button,
} from "semantic-ui-react";
import { LoadingSpinner } from "../../../Shared";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListSites.scss";
import { map } from "lodash";
import _ from "lodash";
import { Permission, Site } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { SiteItem } from "../SiteItem";
import {
  hasPermission,
  isAdmin,
  isMaster,
} from "../../../../utils/checkPermission";
import { ErrorAccessDenied } from "../../../../pages/admin/Error";
import { useLanguage } from "../../../../contexts";
import { BasicModal } from "../../../Shared";
import { SiteForm } from "../SiteForm";

const siteController = new Site();
const permissionController = new Permission();

export function ListSites(props) {
  const { sitesActive, reload, onReload, companySelected, role, companies, onCompanyChange } = props;
  const [sites, setSites] = useState(null);
  const [sitesFilter, setSitesFilter] = useState([]);
  const [permissionByRole, setPermissionsByRole] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const onOpenCloseModal = () => setShowModal((prev) => !prev);

  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  useEffect(() => {
    (async () => {
      try {
        if (role) {
          const response = await permissionController.getPermissionsByRole(
            accessToken,
            role._id,
            true
          );
          setPermissionsByRole(response);
        }
      } catch (error) {
        console.error(error);
        setPermissionsByRole([]);
      }
    })();
  }, [role]);

  useEffect(() => {
    (async () => {
      try {
        setSites(null);
        setSitesFilter([]);
        let response = [];
        if (!companySelected) {
          response = await siteController.getSites(accessToken, sitesActive);
        } else {
          response = await siteController.getSitesByCompany(
            accessToken,
            companySelected,
            sitesActive
          );
        }
        const result = response.map((s) => ({ ...s, key: s._id }));
        setSites(result);
        setSitesFilter(result);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [sitesActive, reload, accessToken, companySelected]);

  if (!sites) return <LoadingSpinner />;

  return (
    <div className="sites-list">
      {(isMaster(role) ||
        isAdmin(role) ||
        hasPermission(permissionByRole, role._id, "sites", "view")) ? (
        <div>
          <div className="sites-list__card">
            <div className="sites-list__filters">
              <div className="sites-list__filter-block sites-list__filter-search">
                <SearchStandardSite
                  dataOrigin={sites}
                  data={sitesFilter}
                  setData={setSitesFilter}
                  t={t}
                />
              </div>
              {companies && companies.length > 0 && (
                <CompanyDropdown
                  companies={companies}
                  value={companySelected}
                  onChange={onCompanyChange}
                  placeholder={t("companies")}
                />
              )}
              {(isMaster(role) ||
                (isAdmin(role) && companySelected) ||
                (hasPermission(permissionByRole, role._id, "sites", "create") && companySelected)) && (
                  <Button icon className="add-site-btn" onClick={() => setShowModal(true)} title={t("create")}>
                    <Icon name="plus" />
                  </Button>
                )}
            </div>

            {sites.length === 0 ? (
              <div className="list-empty-state">
                <Icon name="folder open outline" className="list-empty-state__icon" />
                <p className="list-empty-state__text">{t("there_is_no_site")}</p>
              </div>
            ) : (
              <div className="sites-list__tbl-scroll">
                <Table className="sites-table unstackable rtable-cards row-hover">
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>{t("company_name")}</Table.HeaderCell>
                      <Table.HeaderCell>{t("cuit")}</Table.HeaderCell>
                      <Table.HeaderCell>{t("phone")}</Table.HeaderCell>
                      <Table.HeaderCell>{t("email")}</Table.HeaderCell>
                      <Table.HeaderCell textAlign="center"></Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {map(sitesFilter, (site) => (
                      <SiteItem key={site._id} site={site} onReload={onReload} permissionsByRole={permissionByRole} />
                    ))}
                  </Table.Body>
                </Table>
              </div>
            )}
          </div>

          <BasicModal
            show={showModal}
            close={onOpenCloseModal}
            title={null}
            size="small"
          >
            <SiteForm
              close={onOpenCloseModal}
              onReload={onReload}
              company={companySelected}
            />
          </BasicModal>
        </div>
      ) : (
        <ErrorAccessDenied />
      )}
    </div>
  );
}


function CompanyDropdown(props) {
  const { companies, value, onChange, placeholder } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="sites-list__company-dd" ref={ref}>
      <button type="button" className={`sites-list__company-dd-toggle${open ? " sites-list__company-dd-toggle--open" : ""}`} onClick={() => setOpen((v) => !v)}>
        <span>{placeholder}</span>
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="12" height="12" className={`sites-list__company-dd-arrow${open ? " sites-list__company-dd-arrow--open" : ""}`}>
          <path d="M6 1.5v9M2.5 7 6 10.5 9.5 7"/>
        </svg>
      </button>
      {open && (
        <div className="sites-list__company-dd-menu">
          {companies.map((c) => (
            <button
              type="button"
              key={c._id}
              className={`sites-list__company-dd-item${c._id === value ? " sites-list__company-dd-item--active" : ""}`}
              onClick={() => { onChange && onChange(c._id); setOpen(false); }}
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SearchStandardSite(props) {
  const { dataOrigin, data, setData, t } = props;
  const [state, setState] = useState({
    isLoading: false,
    results: [],
    value: "",
  });

  const handleSearchChange = (e, { value }) => {
    setState({ isLoading: true, value });

    setTimeout(() => {
      if (value.length === 0) {
        setData(dataOrigin);
        setState({ isLoading: false, results: [], value: "" });
        return true;
      }
      const filteredData = dataOrigin.filter((item) =>
        Object.values(item).some((v) =>
          v.toString().toLowerCase().includes(value.toLowerCase())
        )
      );
      setState({
        isLoading: false,
        results: filteredData,
      });
      setData(filteredData);
    }, 300);
  };

  return (
    <div className="search-user-wrapper">
      <img src={searchSvg} alt="search" className="search-user-icon" />
      <input
        className="search-user-input"
        placeholder={t("search")}
        onChange={_.debounce((e) => handleSearchChange(e, { value: e.target.value }), 500, { leading: true })}
      />
    </div>
  );
}
